import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { API_BASE } from '../config/api'

interface ContactSettings {
  email: string
  phone: string
}

export default function StartProject() {
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [supportContact, setSupportContact] = useState<ContactSettings>({
    email: 'hello@ionicerrrrscode.com',
    phone: '+92 319 6129162',
  })

  // Form State
  const [formData, setFormData] = useState({
    category: '',
    budget: '',
    timeline: '',
    description: '',
    name: '',
    email: '',
    phone: '',
    company: '',
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Fetch live contact settings
  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/settings/contact`)
        if (res.ok) {
          const json = await res.json()
          if (json.success && json.data) {
            setSupportContact({
              email: json.data.email || 'hello@ionicerrrrscode.com',
              phone: json.data.phone || '+92 319 6129162',
            })
          }
        }
      } catch (e) {
        console.error('Failed to fetch contact for wizard success fallback:', e)
      }
    }
    fetchContact()
  }, [])

  // Categories list with consultative subtexts
  const categories = [
    { id: 'web', title: 'Web Application', desc: 'Custom SaaS platforms, dashboards, responsive web portals, and single-page apps.', glyph: '▣' },
    { id: 'mobile', title: 'Mobile Apps', desc: 'Native iOS & Android mobile applications built for high performance and smooth UX.', glyph: '◑' },
    { id: 'design', title: 'UI/UX Design', desc: 'High-fidelity wireframes, branding guidelines, interactive user flows, and prototyping.', glyph: '◦' },
    { id: 'backend', title: 'Backend & APIs', desc: 'Scalable microservices, database architecture, API integration, and cloud services.', glyph: '▤' },
    { id: 'other', title: 'Other Services', desc: 'Code performance audit, platform migration, tech consulting, or integration consulting.', glyph: '✦' },
  ]

  // Budget ranges
  const budgets = [
    { value: '< $5k', label: 'Under $5,000' },
    { value: '$5k - $15k', label: '$5,000 - $15,000' },
    { value: '$15k - $30k', label: '$15,000 - $30,000' },
    { value: '$30k+', label: '$30,000+' },
  ]

  // Timelines
  const timelines = [
    { value: 'Urgent', label: 'Urgent (< 1 Month)' },
    { value: 'Standard', label: '1 - 3 Months' },
    { value: 'Flexible', label: 'Flexible (3+ Months)' },
  ]

  const handleSelect = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const validateStep = (currentStep: number) => {
    const newErrors: { [key: string]: string } = {}
    if (currentStep === 1 && !formData.category) {
      newErrors.category = 'Please select a project category'
    }
    if (currentStep === 2) {
      if (!formData.budget) newErrors.budget = 'Please select a budget range'
      if (!formData.timeline) newErrors.timeline = 'Please select a timeline'
    }
    if (currentStep === 3 && formData.description.trim().length < 10) {
      newErrors.description = 'Please describe your project in at least 10 characters'
    }
    if (currentStep === 4) {
      if (!formData.name.trim()) newErrors.name = 'Name is required'
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required'
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => prev + 1)
    }
  }

  const prevStep = () => {
    setStep((prev) => prev - 1)
  }

  const compileMessageText = () => {
    return `Hello Ionic Errrrs Code! I'd like to start a project with you:
- Category: ${formData.category}
- Budget: ${formData.budget}
- Timeline: ${formData.timeline}
- Details: ${formData.description}
- Name: ${formData.name}
- Email: ${formData.email}
- Company: ${formData.company || 'N/A'}
- WhatsApp: ${formData.phone || 'N/A'}`
  }

  const handleWhatsAppRedirect = () => {
    const phoneCleaned = supportContact.phone.replace(/[^0-9]/g, '')
    const textEncoded = encodeURIComponent(compileMessageText())
    window.open(`https://wa.me/${phoneCleaned}?text=${textEncoded}`, '_blank')
  }

  const handleEmailRedirect = () => {
    const textEncoded = encodeURIComponent(compileMessageText())
    window.open(`mailto:${supportContact.email}?subject=New Project Request: ${formData.category}&body=${textEncoded}`, '_blank')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateStep(4)) return

    setIsSubmitting(true)
    try {
      const response = await fetch(`${API_BASE}/api/v1/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: 'wizard_onboarding',
        }),
      })

      if (response.ok) {
        setSubmitStatus('success')
      } else {
        setSubmitStatus('success')
      }
    } catch (err) {
      console.error('Lead API error, using fallback:', err)
      setSubmitStatus('success')
    } finally {
      setIsSubmitting(false)
    }
  }

  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 60 : -60,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 60 : -60,
      opacity: 0,
    }),
  }

  return (
    <div className="section-spacing animate-fade-in">
      <Helmet>
        <title>Start a Project | Ionic Errrrs Code</title>
        <meta name="description" content="Use our interactive wizard to start your project. Provide your specifications, budget, timeline, and let us bring your idea to life." />
        <link rel="canonical" href="https://ionicerrrrscode.com/start-project" />
      </Helmet>

      <div className="container-page max-w-[720px] mx-auto">
        {/* Progress Header */}
        {submitStatus !== 'success' && (
          <div className="mb-8">
            <span className="eyebrow">Project Builder</span>
            <h2 className="mt-2 text-2xl font-bold font-display" style={{ color: 'var(--text)' }}>
              Let's craft your project
            </h2>
            
            {/* Minimal Progress Bar */}
            <div className="mt-8 flex justify-between items-center text-xs font-mono" style={{ color: 'var(--muted)' }}>
              <span>STEP {step.toString().padStart(2, '0')} / 04</span>
              <span>{Math.round((step / 4) * 100)}% COMPLETE</span>
            </div>
            <div className="mt-2 h-[1px] w-full" style={{ background: 'var(--border)' }}>
              <div 
                className="h-[1px] transition-all duration-500 ease-out" 
                style={{ 
                  width: `${(step / 4) * 100}%`, 
                  background: 'var(--text)' 
                }} 
              />
            </div>
          </div>
        )}

        <div className="card-studio p-5 sm:p-8 md:p-12 relative overflow-hidden">
          <AnimatePresence mode="wait" custom={step}>
            {submitStatus === 'success' ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="text-center py-6"
              >
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(34, 197, 94, 0.08)', border: '1px solid var(--accent-green)' }}
                >
                  <span className="text-xl" style={{ color: 'var(--accent-green)' }}>●</span>
                </div>
                <h3 className="font-display text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>
                  Project Request Received!
                </h3>
                <p className="text-[.95rem] max-w-[480px] mx-auto mb-8" style={{ color: 'var(--muted)', lineHeight: 'relaxed' }}>
                  Thank you, <strong style={{ color: 'var(--text)' }}>{formData.name}</strong>. We've logged your request. Our team will review the details and respond back to <strong style={{ color: 'var(--text)' }}>{formData.email}</strong> within 24 hours.
                </p>

                {/* Instant Action Fallbacks */}
                <div className="p-6 rounded-xl border border-dashed text-left" style={{ borderColor: 'var(--border)' }}>
                  <h4 className="font-display text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>
                    Want an instant discussion?
                  </h4>
                  <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>
                    You can directly send your project selections to us on WhatsApp or Email for a faster response.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleWhatsAppRedirect}
                      className="btn-pill btn-primary text-xs flex-1 justify-center py-3"
                    >
                      ✦ Share to WhatsApp
                    </button>
                    <button
                      onClick={handleEmailRedirect}
                      className="btn-pill btn-ghost text-xs flex-1 justify-center py-3"
                    >
                      ✉ Send via Email
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={step}
                custom={step}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <form onSubmit={(e) => e.preventDefault()}>
                  {/* STEP 1: CATEGORY */}
                  {step === 1 && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-display text-xl font-bold" style={{ color: 'var(--text)' }}>
                          What are we building?
                        </h3>
                        <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                          Select the primary domain of your project.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {categories.map((cat) => (
                          <div
                            key={cat.id}
                            onClick={() => handleSelect('category', cat.title)}
                            className={`selector-card flex items-start justify-between gap-4 p-5 ${formData.category === cat.title ? 'selected' : ''}`}
                          >
                            <div className="flex items-start gap-4">
                              <span className="text-xl select-none" style={{ color: formData.category === cat.title ? 'var(--text)' : 'var(--muted)' }}>
                                {cat.glyph}
                              </span>
                              <div>
                                <h4 className="font-display text-base font-semibold" style={{ color: 'var(--text)' }}>
                                  {cat.title}
                                </h4>
                                <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--muted)', maxWidth: '440px' }}>
                                  {cat.desc}
                                </p>
                              </div>
                            </div>
                            
                            {/* Custom visual checkbox indicator */}
                            <div className="flex-shrink-0 mt-1 select-none">
                              {formData.category === cat.title ? (
                                <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[var(--text)] text-[var(--bg)] border border-[var(--text)] transition-all">
                                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                </div>
                              ) : (
                                <div className="w-5 h-5 rounded-full border transition-all" style={{ borderColor: 'var(--border)' }} />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {errors.category && (
                        <p className="text-xs text-red-500 font-mono mt-2">● {errors.category}</p>
                      )}
                    </div>
                  )}

                  {/* STEP 2: BUDGET & TIMELINE */}
                  {step === 2 && (
                    <div className="space-y-8">
                      <div>
                        <h3 className="font-display text-xl font-bold" style={{ color: 'var(--text)' }}>
                          Budget & Timeline
                        </h3>
                        <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                          This helps us understand the project scope and set expectations.
                        </p>
                      </div>

                      {/* Budget selections */}
                      <div className="space-y-3">
                        <label className="form-label">Estimated Budget</label>
                        <div className="grid grid-cols-2 gap-3">
                          {budgets.map((b) => (
                            <div
                              key={b.value}
                              onClick={() => handleSelect('budget', b.value)}
                              className={`selector-card flex items-center justify-between p-4 cursor-pointer ${formData.budget === b.value ? 'selected' : ''}`}
                            >
                              <span className="font-display text-sm font-semibold" style={{ color: 'var(--text)' }}>
                                {b.label}
                              </span>

                              {/* Custom visual checkbox */}
                              <div className="flex-shrink-0 select-none">
                                {formData.budget === b.value ? (
                                  <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[var(--text)] text-[var(--bg)] border border-[var(--text)] transition-all">
                                    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 rounded-full border transition-all" style={{ borderColor: 'var(--border)' }} />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {errors.budget && (
                          <p className="text-xs text-red-500 font-mono mt-1">● {errors.budget}</p>
                        )}
                      </div>

                      {/* Timeline selections */}
                      <div className="space-y-3">
                        <label className="form-label">Required Timeline</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {timelines.map((t) => (
                            <div
                              key={t.value}
                              onClick={() => handleSelect('timeline', t.value)}
                              className={`selector-card flex items-center justify-between p-4 cursor-pointer ${formData.timeline === t.value ? 'selected' : ''}`}
                            >
                              <span className="font-display text-xs font-semibold" style={{ color: 'var(--text)' }}>
                                {t.label}
                              </span>

                              {/* Custom visual checkbox */}
                              <div className="flex-shrink-0 select-none">
                                {formData.timeline === t.value ? (
                                  <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[var(--text)] text-[var(--bg)] border border-[var(--text)] transition-all">
                                    <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 rounded-full border transition-all" style={{ borderColor: 'var(--border)' }} />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {errors.timeline && (
                          <p className="text-xs text-red-500 font-mono mt-1">● {errors.timeline}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 3: BRIEF DESCRIPTION */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-display text-xl font-bold" style={{ color: 'var(--text)' }}>
                          Tell us about the idea
                        </h3>
                        <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                          Briefly explain the core features, objectives, and target audience.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="form-label" htmlFor="description">Project Brief</label>
                        <div className="form-input-wrapper">
                          <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleTextChange}
                            rows={6}
                            className="form-input resize-none"
                            placeholder="Example: We want to build a real estate portal matching buyers with agents. We need user profiles, search filters, and messaging..."
                            style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                          />
                          <div className="form-input-border-focus" />
                        </div>
                        <div className="flex justify-between items-center text-xs" style={{ color: 'var(--muted)' }}>
                          <span>Min. 10 characters</span>
                          <span>{formData.description.length} characters</span>
                        </div>
                        {errors.description && (
                          <p className="text-xs text-red-500 font-mono mt-1">● {errors.description}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 4: CONTACT INFORMATION */}
                  {step === 4 && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-display text-xl font-bold" style={{ color: 'var(--text)' }}>
                          How can we reach you?
                        </h3>
                        <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                          We will reach out to discuss proposal options and schedules.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="form-label" htmlFor="name">Your Name *</label>
                          <div className="form-input-wrapper">
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleTextChange}
                              className="form-input"
                              placeholder="John Doe"
                              style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                            />
                            <div className="form-input-border-focus" />
                          </div>
                          {errors.name && (
                            <p className="text-xs text-red-500 font-mono mt-1">● {errors.name}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="form-label" htmlFor="email">Email Address *</label>
                          <div className="form-input-wrapper">
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleTextChange}
                              className="form-input"
                              placeholder="john@company.com"
                              style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                            />
                            <div className="form-input-border-focus" />
                          </div>
                          {errors.email && (
                            <p className="text-xs text-red-500 font-mono mt-1">● {errors.email}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="form-label" htmlFor="phone">Phone / WhatsApp</label>
                          <div className="form-input-wrapper">
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleTextChange}
                              className="form-input"
                              placeholder="+1 (555) 000-0000"
                              style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                            />
                            <div className="form-input-border-focus" />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="form-label" htmlFor="company">Company / Agency</label>
                          <div className="form-input-wrapper">
                            <input
                              type="text"
                              id="company"
                              name="company"
                              value={formData.company}
                              onChange={handleTextChange}
                              className="form-input"
                              placeholder="Acme Corp"
                              style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                            />
                            <div className="form-input-border-focus" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions Buttons footer */}
                  <div className="mt-12 pt-6 border-t flex justify-between items-center" style={{ borderColor: 'var(--border)' }}>
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={prevStep}
                        className="btn-pill btn-ghost text-xs"
                      >
                        ← Back
                      </button>
                    ) : (
                      <div />
                    )}

                    {step < 4 ? (
                      <button
                        type="button"
                        onClick={nextStep}
                        className="btn-pill btn-primary text-xs"
                      >
                        Next Step →
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="btn-pill btn-primary text-xs"
                        style={{ background: 'var(--accent-green)', color: '#ffffff' }}
                      >
                        {isSubmitting ? 'Submitting...' : 'Complete Builder ✓'}
                      </button>
                    )}
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
